import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import { Box, Button, Chip, Stack, styled, SxProps, Typography } from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, To } from "react-router";
import { assetService } from "../../../../Core/Api/services";
import { AclTypes } from "../../../../Core/Api/Types/acl-types";
import { DbAssetObjectInfos } from "../../../../Core/Api/Types/db-asset-object-infos";
import { Licences } from "../../../../Core/Api/Types/licences";
import { ObjectLayer } from "../../../../Core/Api/Types/object-layer";
import { ObjectType } from "../../../../Core/Api/Types/object-type";
import { GetMetadataByTagName } from "../../../../Core/Utils/asset-object-utils";
import { UserHasAcl, UserHasLicence } from "../../../../Utils/helpers";
import { hexToRgbString } from "../../../../Utils/Helpers/cssUtils";
import useBasketActions from "../../../../Utils/Hooks/useBasketActions";
import useCollectionActions from "../../../../Utils/Hooks/useCollectionActions";
import useCustomSnackbar from "../../../../Utils/Hooks/useCustomSnackbar";
import Cover from "../../../Misc/Cover";
import { ModalContext } from "../../../Modals";
import ModalCreateShare from "../../../Modals/ModalCreateShare/modal-create-share";
import ModalPreview, { ModalPreviewProps } from "../../../Modals/ModalPreview/modal-video-preview";

export type PushContentSlideProps = {
  className?: string;
  sx?: SxProps;
  id: number;
  to: To;
  cover: string;
  title: string;
  casting: string;
  tags: string[];
};

const PushContentSlide: React.FC<PushContentSlideProps> = (props) => {
  const { t } = useTranslation();
  const { id, to, cover, title, casting, tags, ...otherProps } = props;
  const { AddItemByIdToCollection } = useCollectionActions();
  const { addObjectToBasket } = useBasketActions();
  const [trailerLoading, setTrailerLoading] = useState(false);
  const [trailerContent, setTrailerContent] = useState<DbAssetObjectInfos | null | undefined>(null);
  const [isSeason, setIsSeason] = useState<boolean | null | undefined>(null);

  // modal context
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const enqueueCustomSnackbar = useCustomSnackbar();

  const addToCollection = () => {
    AddItemByIdToCollection(id);
  };

  const addToBasket = () => {
    assetService.GetObjectLightById(id).then((response) => {
      if (response.graph) addObjectToBasket(response.graph);
    });
  };

  const handleShare = () => {
    if (!openModal) return;

    assetService.GetObjectLightById(id).then((response) => {
      openModal(ModalCreateShare, { object: response.graph });
    });
  };

  const openTrailerPlayerModal = () => {
    if (trailerContent) {
      console.debug("home trailer found for titleId = " + id);
      if (!openModal) return;

      openModal<ModalPreviewProps>(ModalPreview, {
        objectId: trailerContent.id,
        episodeNb: isSeason ? 1 : null,
        type: ObjectType.Video,
        title: t("modal:title.watchTrailer", { title }),
        closeModal: () => {},
      });
    } else enqueueCustomSnackbar(t("alert:primary.noTrailerAvailableForThisTitle"), "primary");
  };

  const onMouseEnter = () => {
    if (trailerContent !== null) return;
    setTrailerLoading(true);
    assetService.GetObjectLightById(id, 1).then((response) => {
      if (
        response &&
        response.graph &&
        response.graph.childObjects &&
        response.graph.childObjects.filter((x) => x.layer === ObjectLayer.Content).length > 0
      ) {
        let match = response.graph.childObjects.find(
          (x) => x.layer === ObjectLayer.Content && GetMetadataByTagName(x, "contentType")?.value === "Trailer"
        );
        setTrailerContent(match);
        let isSeason = GetMetadataByTagName(response.graph, "titleType")?.value !== "Movie";
        setIsSeason(isSeason);
        if (match) setTrailerLoading(false);
      }
    });
  };

  return (
    <StyledBox {...otherProps} onMouseEnter={() => onMouseEnter()}>
      <Cover img={{ src: cover, alt: title }} />

      <Stack className="push-content__text" direction="column">
        <Stack>
          <Typography
            component="h3"
            variant="h2"
            fontWeight={700}
            sx={{ mb: 1.5, "& a:hover": { textDecoration: "underline" } }}
          >
            <Link to={to}>{title}</Link>
          </Typography>
          {casting && (
            <Typography component="p" color="text.tier" variant="subtitle2" fontWeight={400}>
              {t("common:with")} {""}
              <Typography component="span" color="text.secondary" fontWeight={600} variant="subtitle2">
                {casting}
              </Typography>
            </Typography>
          )}
        </Stack>
        <Stack className="push-content__hidden" direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button
            onClick={openTrailerPlayerModal}
            variant="white"
            startIcon={<PlayArrowIcon />}
            loading={trailerLoading}
          >
            {t("common:trailer")}
          </Button>

          <Button onClick={addToCollection}>
            <BookmarkBorderOutlinedIcon />
          </Button>

          <Button onClick={addToBasket}>
            <ShoppingBagOutlinedIcon />
          </Button>

          {UserHasAcl(AclTypes.aclShareCreate) && UserHasLicence(Licences.ShareScreening) && (
            <Button onClick={handleShare}>
              <ShareOutlinedIcon />
            </Button>
          )}
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ ml: 2 }}>
        {tags.map((tag) => (
          <Chip key={tag} label={tag} />
        ))}
      </Stack>
    </StyledBox>
  );
};

const StyledBox = styled(Box)(({ theme }) => ({
  position: "relative",
  cursor: "default",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-end",
  width: "100%",
  height: 420,
  borderRadius: 12,
  overflow: "hidden",
  boxShadow:
    "0px 34px 24px -24px rgba(${hexToRgbString(theme.palette.background.paper)}, 0.8), 0px 24px 48px -24px rgba(245, 92, 44, 0.25)",
  padding: theme.spacing(3),
  "> div": {
    position: "relative",
    zIndex: 1,
  },
  ".MuiChip-root": {
    backdropFilter: "blur(12px)",
    color: theme.palette.text.primary,
    background: `rgba(${hexToRgbString(theme.palette.background[400])}, .5)`,
    // mixBlendMode: "difference",
  },
  "&:before, &:after": {
    content: `""`,
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: "none",
    zIndex: 1,
  },
  "&:before": {
    background: `linear-gradient(190deg, rgba(${hexToRgbString(
      theme.palette.background.paper
    )}, 0) 42%, rgba(${hexToRgbString(theme.palette.background.paper)}, 0.64) 76.08%, rgba(${hexToRgbString(
      theme.palette.background.paper
    )}, 0.9) 86.43%)`,
  },
  "&:after": {
    background: `linear-gradient(0deg, rgba(${hexToRgbString(
      theme.palette.background.paper
    )}, 0.4), rgba(${hexToRgbString(theme.palette.background.paper)}, 0.4))`,
    opacity: 0,
    transition: "opacity .3s",
  },

  ".push-content__text": {
    transform: "translateY(56px)",
    transition: "transform .6s",
  },
  ".push-content__hidden": {
    transform: "translateY(32px)",
    transition: "transform .6s",
  },
  "&:hover": {
    ".push-content__text, .push-content__hidden": {
      transform: "translateY(0)",
    },
    ".push-content__text": {
      transition: ".4s transform .2s",
    },
    ".push-content__hidden": {
      transition: "transform .6s",
    },
  },
}));

export default PushContentSlide;
